import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { BrnDialogDescription, BrnDialogRef, BrnDialogTitle, injectBrnDialogContext } from '@spartan-ng/brain/dialog';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { DIALOG_TITLE_CLASS, DialogSheetComponent, dialogSheetHostClass } from '@lite-workspace/shared/helm-ui/dialog';
import type { TemplateLicenceType } from '../domain/entities/design.models';

export interface PartnerPurchaseIntentDialogContext {
  type: TemplateLicenceType;
  packName: string;
  priceRub: number | null;
  studioName: string;
  /** Адрес магазина студии: вкладку открывает САМ клик по «Перейти к оплате», а не продолжение
   *  закрытия — Spartan закрывает диалог через анимацию, и к тому моменту жест уже потерян. */
  shopUrl: string;
  /** The photographer's own email — the shop order must carry it to be matched back to him; empty = no line. */
  email: string;
  /** The rental opens the studio's one shared rental product, not this pack's page. */
  sharedRental: boolean;
}

/** Чем кончился клик «Перейти к оплате»: вкладка магазина открылась или её съел блокировщик. */
export type PurchaseIntentResult = 'opened' | 'blocked' | undefined;

const rub = new Intl.NumberFormat('ru-RU');

/** The last stop before the money: the shop opens in another tab and a pending request is created behind
 *  the photographer's back, so the three steps are spelled out while the click can still be taken back. */
@Component({
  selector: 'app-partner-purchase-intent-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HlmButton, DialogSheetComponent, BrnDialogTitle, BrnDialogDescription],
  host: { '[class]': 'hostClass' },
  template: `
    <hlm-dialog-sheet>
      <ng-template>
        <h2 brnDialogTitle [class]="titleClass">{{ title }}</h2>
        <p brnDialogDescription class="text-base leading-relaxed text-ink">{{ lead }}</p>
        <ol class="mt-7 space-y-5">
          @for (step of steps; track step.title; let i = $index) {
            <li class="flex items-start gap-3.5">
              <span
                class="grid size-7 shrink-0 place-items-center rounded-full border border-line bg-bg2 text-xs font-bold text-c2"
                aria-hidden="true"
                >{{ i + 1 }}</span
              >
              <span class="min-w-0 flex-1">
                <span class="block text-sm font-bold text-ink">{{ step.title }}</span>
                <span class="mt-1 block text-xs leading-relaxed text-c2">{{ step.note }}</span>
                @if (i === 0 && ctx.email) {
                  <span class="mt-1 block text-xs leading-relaxed text-c2"
                    >В заказе укажите почту <span class="font-bold text-ink">{{ ctx.email }}</span></span
                  >
                }
              </span>
            </li>
          }
        </ol>
        <div class="mt-8 flex gap-3">
          <button hlmBtn type="button" variant="outline" class="h-12 flex-1 justify-center" (click)="cancel()">Отмена</button>
          <button hlmBtn type="button" class="h-12 flex-1 justify-center" (click)="confirm()">Перейти к оплате</button>
        </div>
      </ng-template>
    </hlm-dialog-sheet>
  `,
})
export class PartnerPurchaseIntentDialogComponent {
  protected readonly hostClass = dialogSheetHostClass('m', 'p-9');
  protected readonly titleClass = DIALOG_TITLE_CLASS;
  protected readonly ctx = injectBrnDialogContext<PartnerPurchaseIntentDialogContext>();
  private readonly dialogRef = inject(BrnDialogRef);

  private readonly purchase = this.ctx.type === 'purchase';
  private readonly action = this.purchase ? 'Купить' : 'Арендовать';

  protected readonly title =
    this.ctx.priceRub === null
      ? `${this.action} «${this.ctx.packName}»`
      : `${this.action} «${this.ctx.packName}» за ${rub.format(this.ctx.priceRub)} ₽`;

  protected readonly lead = this.purchase
    ? 'Шаблон останется у вас навсегда — применяйте в любом проекте'
    : 'Аренда — это один кредит: шаблон можно применить в одном проекте';

  protected readonly steps: readonly { title: string; note: string }[] = [
    {
      title: 'Оплата на сайте студии',
      note:
        !this.purchase && this.ctx.sharedRental
          ? 'Откроем товар «Аренда шаблона», он общий для всех шаблонов'
          : `Откроем сайт ${this.ctx.studioName} в новой вкладке — оплата проходит там, напрямую студии`,
    },
    {
      title: 'Магазин подтвердит оплату',
      note: 'Это происходит автоматически, ждать ответа студии не нужно',
    },
    {
      title: 'Шаблон появится в «Моих шаблонах»',
      note: this.purchase
        ? 'Оттуда его можно применить в любом проекте'
        : 'Оттуда его можно применить в одном проекте на ваш выбор',
    },
  ];

  protected confirm(): void {
    // Открываем вкладку ПЕРВЫМ действием обработчика: любой await или закрытие диалога до неё рвут
    // пользовательский жест, и браузер блокирует окно.
    // Без токена `noopener`: с ним `window.open` возвращает `null` ВСЕГДА, даже когда вкладка
    // открылась, — читаемый возврат и `noopener` взаимоисключающи, и на нём исход становился
    // 'blocked' на каждой покупке. `noreferrer` не годится по той же причине: он влечёт `noopener`.
    // Ссылку на нас рвём сами, синхронно: tabnabbing (дочерняя страница → родитель) этим закрыт
    // полностью. Слабее `noopener` мы ровно в одном — вкладка остаётся в той же browsing context
    // group (общий процесс, `window.name`, `postMessage`); адрес магазина берётся из переменной
    // деплоя, а не из пользовательского ввода, так что группа контекстов ничем не рискует.
    const opened = window.open(this.ctx.shopUrl, '_blank');
    if (opened) opened.opener = null;
    this.dialogRef.close(opened ? 'opened' : 'blocked');
  }

  protected cancel(): void {
    this.dialogRef.close(undefined);
  }
}
